import { Component, type ErrorInfo, type ReactNode } from "react"
import { AlertTriangle } from "lucide-react"
import { captureError } from "@/lib/telemetry/sentry"

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo)
    captureError(error, { componentStack: errorInfo.componentStack ?? undefined })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex h-full min-h-[200px] items-center justify-center p-6">
          <div className="w-full max-w-sm rounded-[var(--radius-panel)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-secondary)] p-6 text-center">
            <AlertTriangle className="mx-auto h-8 w-8 text-[var(--color-accent-amber)]" />
            <h2 className="mt-3 text-sm font-semibold text-[var(--color-text-primary)]">
              Something went wrong
            </h2>
            {import.meta.env.DEV && this.state.error && (
              <p className="mt-2 text-xs text-[var(--color-text-muted)] break-words">
                {this.state.error.message}
              </p>
            )}
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="mt-4 rounded-[var(--radius-card)] bg-[var(--color-accent-blue)] px-4 py-1.5 text-xs font-medium text-white transition-colors hover:opacity-90"
            >
              Try again
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
